import React, { useMemo, useState, useCallback } from 'react'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { Skeleton } from '@/components/ui/skeleton'
import { ServiceInformationProps } from '@/modules/Service/types'
import { useServiceWithAddonConfig } from '@/modules/Service/services'
import { useTranslation } from 'react-i18next'
import {
    transformVCloudData,
    filterVCloudRows,
    detectColumnVisibility,
    getVCloudRowClassName,
    VCloudRow,
} from '../Information/vcloud-config'
import { createVCloudSettingColumns } from './vcloud-setting-columns'

const Setting: React.FC<ServiceInformationProps> = ({ service }) => {
    const { t } = useTranslation(['services', 'customer'])

    // Fetch config options with addons - same as Information
    const { data, renderState, isLoading, isPending } =
        useServiceWithAddonConfig(service?.service_id)

    // Expanded state for service groups (default: all collapsed)
    const [expandedServices, setExpandedServices] = useState<Set<string>>(
        new Set()
    )

    // Toggle expand/collapse for a service
    const handleToggleExpand = useCallback((serviceId: string) => {
        setExpandedServices((prev) => {
            const next = new Set(prev)
            if (next.has(serviceId)) {
                next.delete(serviceId)
            } else {
                next.add(serviceId)
            }
            return next
        })
    }, [])

    // Transform data using same transformer as Information
    const allRows = useMemo(
        () => transformVCloudData({ service, data, t }),
        [data, service, t]
    )

    // Filter rows based on expanded state - same as Information
    const rows = useMemo(
        () => filterVCloudRows(allRows, expandedServices),
        [allRows, expandedServices]
    )

    // Detect column visibility - same as Information
    const columnVisibility = useMemo(
        () => detectColumnVisibility(allRows),
        [allRows]
    )

    // Create Setting-specific columns (subset of Information columns)
    const columns = useMemo(
        () =>
            createVCloudSettingColumns({
                t,
                expandedServices,
                onToggleExpand: handleToggleExpand,
                columnVisibility,
            }),
        [t, expandedServices, handleToggleExpand, columnVisibility]
    )

    // Handle loading/error states
    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    if (isLoading || isPending) {
        return (
            <div className="space-y-2 p-4">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
            </div>
        )
    }

    // Show PAYG message if no commitment data
    if (allRows.length === 0) {
        return (
            <div className="flex items-center justify-center p-8 bg-blue-50 rounded-md border border-blue-200 mt-2">
                <div className="text-center">
                    <p className="text-2xl font-semibold text-blue-700 mb-2">
                        {t('services:info.payg', { defaultValue: 'PAYG' })}
                    </p>
                    <p className="text-sm text-gray-600">
                        {t('services:info.payg-description', {
                            defaultValue:
                                'Pay As You Go - No commitment resources',
                        })}
                    </p>
                </div>
            </div>
        )
    }

    return (
        <div className="mt-2">
            <DataTableCustomized<VCloudRow>
                columns={columns}
                rows={rows}
                rowKey={(row) => row._rowId}
                rowClassName={getVCloudRowClassName}
                size="small"
                toolbar={{
                    settings: {
                        show: true,
                        storageKey: 'vcloud-setting',
                    },
                }}
            />
        </div>
    )
}

export default Setting
